import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  Typography,
} from '@mui/material';
import { MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  NAV_BACKGROUND_COLOR,
  NAV_HOVER_COLOR,
  NAV_HIGHLIGHT_TEXT_COLOR,
  NAV_SWITCH_SX,
} from 'portal/src/theme/constants';

import ImageLoader from '../../components/common/ImageLoader';
import useProductMenuItemsLegacy from '../../navigation/useProductMenuItemsLegacy';
import { useLayoutPreference } from '../LayoutPreference/useLayoutPreference';

const logoPng = import.meta.env.VITE_LOGO || 'logo.png';

/** @deprecated Temporary dual-layout rollout legacy menu. Remove when legacy layout is retired. */
const MenuLegacy = ({
  open,
  menuItemClick,
}: {
  open: boolean;
  menuItemClick: () => void;
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setUseNewLayout, useNewLayout } = useLayoutPreference();
  const menuItems = useProductMenuItemsLegacy();

  let selected = 0;
  if (pathname !== null) {
    selected = menuItems.findIndex((item) => pathname.startsWith(item.link));
  }

  const isPlainLeftClick = (event: MouseEvent<HTMLElement>) =>
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey;

  const drawer = (
    <Box display="flex" flexDirection="column" height="100%">
      <Box padding={2}>
        <ImageLoader
          src={`/${logoPng}`}
          alt="logo"
          effect="opacity"
          effectDuration={600}
          wrapperStyle={{ transitionDelay: '1s', width: '100%' }}
        />
      </Box>
      <List
        component={'nav'}
        sx={{
          marginTop: 4,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          '& .MuiListItemButton-root:hover': {
            '&, & .MuiListItemIcon-root': {
              color: '#fff6',
            },
          },
        }}
      >
        {menuItems.map((item, index) => (
          <ListItemButton
            key={index}
            component="a"
            href={item.link}
            onClick={(event) => {
              if (!isPlainLeftClick(event)) {
                return;
              }

              event.preventDefault();
              navigate(item.link || '/');
              menuItemClick();
            }}
            sx={{
              color:
                index === selected ? NAV_HIGHLIGHT_TEXT_COLOR : 'common.white',
              backgroundColor:
                index === selected ? 'primary.light' : 'transparent',
              '&:hover': {
                color: NAV_HIGHLIGHT_TEXT_COLOR,
                backgroundColor:
                  index === selected ? 'primary.light' : NAV_HOVER_COLOR,
              },
              borderRight: index === selected ? '5px solid' : 'none',
              borderColor: 'secondary.main',
            }}
          >
            <ListItemText>{item.title}</ListItemText>
          </ListItemButton>
        ))}
      </List>
      <Box
        mt="auto"
        px={2}
        pt={1}
        pb={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          sx={{
            color: 'primary.contrastText',
            fontSize: 16,
          }}
        >
          New Layout
        </Typography>
        <Switch
          checked={useNewLayout}
          onChange={(_, checked) => {
            void setUseNewLayout(checked);
          }}
          sx={NAV_SWITCH_SX}
          slotProps={{ track: { sx: { backgroundColor: 'grey.500' } } }}
        />
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop Version */}
      <Box
        sx={{
          backgroundColor: NAV_BACKGROUND_COLOR,
          display: { lg: 'block', xs: 'none' },
          height: '100vh',
          py: 1,
        }}
      >
        {drawer}
      </Box>
      {/* Mobile Version */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={() => menuItemClick()}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { lg: 'none', xs: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 240,
            backgroundColor: NAV_BACKGROUND_COLOR,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default MenuLegacy;
